import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileText, Music, AlertCircle, CheckCircle, X } from 'lucide-react';
import api from '../../services/api';
import Papa from 'papaparse';
import { normalizeCsvRows, validateCsvRows } from '../../utils/csvUpload';

const labelClassName = 'block text-sm font-medium text-gray-300 mb-2';
const CSV_TEMPLATE_KEY = 'resources/submission-metadata-template.csv';

const getDropzoneClassName = (isDragActive) =>
  isDragActive
    ? 'border-gold-50 bg-gold-50/10'
    : 'border-white/20 hover:border-gold-50/60';

const isAudioFile = (file) =>
  file.type.startsWith('audio/') || /\.(mp3|wav|m4a|flac)$/i.test(file.name);

const isImageFile = (file) =>
  file.type.startsWith('image/') || /\.(png|jpe?g)$/i.test(file.name);

const findMatchingFileName = (csvFileName, fileNames) => {
  if (!csvFileName) {
    return null;
  }

  const csvFileNameWithoutExt = csvFileName.replace(/\.[^/.]+$/, '');

  for (const fileName of fileNames) {
    const fileWithoutExt = fileName.replace(/\.[^/.]+$/, '');

    if (fileName === csvFileName || fileWithoutExt === csvFileNameWithoutExt) {
      return fileName;
    }

    if (
      fileWithoutExt.includes(csvFileNameWithoutExt)
      || csvFileNameWithoutExt.includes(fileWithoutExt)
    ) {
      return fileName;
    }
  }

  return null;
};

export const BatchUploadSection = forwardRef(function BatchUploadSection(
  {
    embedded = false,
    hideCsvDropzone = false,
    incomingCsvFile = null,
    onIncomingCsvConsumed,
  },
  ref,
) {
  const [csvFile, setCsvFile] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [csvData, setCsvData] = useState([]);
  const [knownArtistIds, setKnownArtistIds] = useState(null);

  useEffect(() => {
    const fetchArtists = async () => {
      try {
        const response = await api.get('/api/artists');
        setKnownArtistIds(new Set(response.data.artists.map((artist) => artist.id)));
      } catch (error) {
        console.error('Failed to fetch artists for CSV validation:', error);
      }
    };

    fetchArtists();
  }, []);

  const validateCsvAndFiles = useCallback((parsedCsvData, files, artistIds = knownArtistIds) => {
    const errors = [...validateCsvRows(parsedCsvData, { knownArtistIds: artistIds || undefined })];
    const audioFileNames = files.filter(isAudioFile).map((file) => file.name);
    const imageFileNames = files.filter(isImageFile).map((file) => file.name);

    parsedCsvData.forEach((row, index) => {
      if (!row.url) {
        return;
      }

      if (files.length > 0) {
        const matchingAudio = findMatchingFileName(row.url, audioFileNames);
        if (!matchingAudio) {
          errors.push(`Row ${index + 2}: No matching audio file found for "${row.url}"`);
        }

        if (row.image && !findMatchingFileName(row.image, imageFileNames)) {
          errors.push(`Row ${index + 2}: No matching image file found for "${row.image}"`);
        }
      }
    });

    return errors;
  }, [knownArtistIds]);

  useEffect(() => {
    if (csvData.length > 0) {
      setValidationErrors(validateCsvAndFiles(csvData, mediaFiles));
    }
  }, [csvData, mediaFiles, knownArtistIds, validateCsvAndFiles]);

  const processCsvFile = useCallback((file) => {
    if (!file) return;
    setCsvFile(file);

    Papa.parse(file, {
      header: true,
      complete: (results) => {
        const rows = normalizeCsvRows(results.data);
        setCsvData(rows);
        setValidationErrors(validateCsvAndFiles(rows, mediaFiles));
      },
      error: (error) => {
        console.error('CSV parsing error:', error);
        setValidationErrors([`CSV parsing error: ${error.message}`]);
      },
    });
  }, [mediaFiles, validateCsvAndFiles]);

  useImperativeHandle(ref, () => ({
    loadCsvFile: processCsvFile,
  }));

  useEffect(() => {
    if (incomingCsvFile) {
      processCsvFile(incomingCsvFile);
      onIncomingCsvConsumed?.();
    }
  }, [incomingCsvFile, onIncomingCsvConsumed, processCsvFile]);

  const onCsvDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      processCsvFile(acceptedFiles[0]);
    }
  };

  const onMediaDrop = (acceptedFiles) => {
    const newFiles = [...mediaFiles, ...acceptedFiles];
    setMediaFiles(newFiles);

    if (csvData.length > 0) {
      const errors = validateCsvAndFiles(csvData, newFiles);
      setValidationErrors(errors);
    }
  };

  const { getRootProps: getCsvRootProps, getInputProps: getCsvInputProps, isDragActive: isCsvDragActive } = useDropzone({
    onDrop: onCsvDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    noClick: hideCsvDropzone,
    noKeyboard: hideCsvDropzone,
  });

  const { getRootProps: getMediaRootProps, getInputProps: getMediaInputProps, isDragActive: isMediaDragActive } = useDropzone({
    onDrop: onMediaDrop,
    accept: {
      'audio/*': ['.mp3', '.wav', '.m4a', '.flac'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
    },
    multiple: true,
  });

  const handleSubmit = async () => {
    if (!csvFile || mediaFiles.filter(isAudioFile).length === 0) {
      setUploadStatus({
        type: 'error',
        message: 'Please select both a CSV file and matching audio files',
      });
      return;
    }

    const submitErrors = validateCsvAndFiles(csvData, mediaFiles);
    if (submitErrors.length > 0) {
      setValidationErrors(submitErrors);
      setUploadStatus({
        type: 'error',
        message: 'Please fix the validation errors before uploading',
      });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      const formData = new FormData();
      formData.append('csv_file', csvFile);

      mediaFiles.forEach((file) => {
        formData.append('audio_files', file);
      });

      const response = await api.post('/api/submissions/batch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setUploadStatus({
        type: 'success',
        message: `Batch upload completed! Processed ${response.data.results.length} submissions.`,
        results: response.data.results,
      });

      setCsvFile(null);
      setMediaFiles([]);
      setCsvData([]);
      setValidationErrors([]);
    } catch (error) {
      console.error('Batch upload error:', error);
      const apiValidationErrors = error.response?.data?.validationErrors;
      if (apiValidationErrors?.length) {
        setValidationErrors(apiValidationErrors);
      }
      setUploadStatus({
        type: 'error',
        message: error.response?.data?.error || 'Batch upload failed',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async (e) => {
    e.preventDefault();

    try {
      const { data } = await api.get('/api/media/presign', {
        params: { url: CSV_TEMPLATE_KEY },
      });

      if (!data.presignedUrl) {
        throw new Error('No download URL returned');
      }

      window.location.assign(data.presignedUrl);
    } catch (error) {
      console.error('Template download error:', error);
      setUploadStatus({
        type: 'error',
        message: 'Failed to download CSV template',
      });
    }
  };

  const removeMediaFile = (index) => {
    const newFiles = mediaFiles.filter((_, i) => i !== index);
    setMediaFiles(newFiles);

    if (csvData.length > 0) {
      const errors = validateCsvAndFiles(csvData, newFiles);
      setValidationErrors(errors);
    }
  };

  const panel = (
    <div className={embedded ? 'p-6' : 'library-page-form'}>
      {uploadStatus && (
        <div
          className={`mb-6 p-4 rounded-md ${
            uploadStatus.type === 'success'
              ? 'bg-green-900/40 border border-green-700'
              : 'bg-red-900/40 border border-red-700'
          }`}
        >
          <div className="flex items-center space-x-2">
            {uploadStatus.type === 'success' ? (
              <CheckCircle className="h-5 w-5 text-green-400" />
            ) : (
              <AlertCircle className="h-5 w-5 text-red-400" />
            )}
            <p
              className={`text-sm ${
                uploadStatus.type === 'success' ? 'text-green-200' : 'text-red-200'
              }`}
            >
              {uploadStatus.message}
            </p>
          </div>

          {uploadStatus.results?.some((result) => !result.success) && (
            <div className="mt-4">
              <h4 className="font-medium text-white mb-2">Issues</h4>
              <div className="space-y-1">
                {uploadStatus.results
                  .filter((result) => !result.success)
                  .map((result, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <AlertCircle className="h-4 w-4 text-red-400" />
                      <span className="text-red-200">
                        {result.title}: {result.error}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className={`grid grid-cols-1 ${hideCsvDropzone ? '' : 'md:grid-cols-2'} gap-6`}>
        {!hideCsvDropzone && (
          <div>
            <div className="flex items-center gap-3 mb-2">
              <label className="block text-sm font-medium text-gray-300">CSV Metadata File *</label>
              <button
                type="button"
                onClick={handleDownloadTemplate}
                className="text-sm text-gold-50 hover:text-gold-100"
              >
                Download CSV Template
              </button>
            </div>
            <div
              {...getCsvRootProps()}
              className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${getDropzoneClassName(isCsvDragActive)}`}
            >
              <input {...getCsvInputProps()} />
              <FileText className="mx-auto h-12 w-12 text-gray-500 mb-4" />
              {csvFile ? (
                <div>
                  <p className="text-sm font-medium text-white">{csvFile.name}</p>
                  <p className="text-xs text-gray-400">{(csvFile.size / 1024).toFixed(2)} KB</p>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-400">
                    {isCsvDragActive
                      ? 'Drop the CSV file here'
                      : 'Drag & drop a CSV file here, or click to select'}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Must be a valid CSV with metadata for each track
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {hideCsvDropzone && csvFile && (
          <div className="mb-2 text-sm text-gray-400">
            CSV loaded: <span className="text-white font-medium">{csvFile.name}</span>
          </div>
        )}

        <div className={hideCsvDropzone ? 'md:col-span-1' : ''}>
          <label className={labelClassName}>Audio and Image Files *</label>
          <div
            {...getMediaRootProps()}
            className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${getDropzoneClassName(isMediaDragActive)}`}
          >
            <input {...getMediaInputProps()} />
            <Music className="mx-auto h-12 w-12 text-gray-500 mb-4" />
            {mediaFiles.length > 0 ? (
              <div className="space-y-1 text-left">
                {mediaFiles.map((file, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm text-white">
                    <span className="truncate pr-2">{file.name}</span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeMediaFile(idx);
                      }}
                      className="ml-2 text-gray-400 hover:text-white flex-shrink-0"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-400">
                  {isMediaDragActive
                    ? 'Drop files here'
                    : 'Drag & drop audio and image files here, or click to select'}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Supports MP3, WAV, M4A, FLAC, PNG, JPG, JPEG (max 100MB each)
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {validationErrors.length > 0 && (
        <div className="mt-6 bg-yellow-900/30 border border-yellow-700/50 rounded-md p-4">
          <div className="flex items-start space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-yellow-200 font-medium mb-1">Validation Errors</p>
              <ul className="list-disc list-inside text-sm text-yellow-100/90 space-y-0.5">
                {validationErrors.map((err, idx) => (
                  <li key={idx}>{err}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="mt-8 pt-6 border-t border-white/10">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={
            uploading
            || validationErrors.length > 0
            || csvData.length === 0
            || (csvData.length > 0 && !knownArtistIds)
          }
          className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-gold-50 hover:bg-gold-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uploading ? 'Uploading...' : 'Upload Batch'}
        </button>
      </div>
    </div>
  );

  if (embedded) {
    return panel;
  }

  return (
    <div className="library-page">
      <div className="library-page-header relative overflow-hidden group">
        <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/[0.095] to-transparent opacity-0 group-hover:opacity-100 transition-all duration-700 transform -translate-x-full group-hover:translate-x-full" />
        <div className="relative z-10 p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-white">Batch Track Upload</h1>
              <p className="text-sm text-gray-400 mt-1">
                Upload a CSV metadata file and matching audio and image files
              </p>
            </div>
          </div>
        </div>
      </div>
      <div className="library-page-scroll">
        <div className="library-page-panel">{panel}</div>
      </div>
    </div>
  );
});

export default BatchUploadSection;
